// Copyright ©️ 2026 eVolve MEP, LLC

import type {
  CreateTableViewRequest,
  TableView,
  TableViewId,
  TableViewWithGridState,
  UpdateTableViewRequest,
} from '@evolvemep/api-ts';
import { TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import type { GridApi } from 'ag-grid-enterprise';

import { useFormModal } from 'components/Mantine/Modals/FormModal';
import { stringifyGridStateJson } from 'helpers/ag-grid/TableViewPanel/utils';
import type { StrongOmit } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';

export type CreateOrEditTableViewFormProps = {
  tableView: TableView | null;
  gridApi: GridApi;
  createTableView: (
    newTableView: StrongOmit<CreateTableViewRequest, 'tableViewType'>,
  ) => Promise<TableViewWithGridState>;
  onTableViewCreated: (tableView: TableViewWithGridState) => void;
  updateTableView: (id: TableViewId, updatedTableView: UpdateTableViewRequest) => Promise<TableViewWithGridState>;
  onTableViewUpdated: (tableView: TableViewWithGridState) => void;
};

type FormBody = StrongOmit<CreateTableViewRequest, 'tableViewType' | 'gridState'>;

export const CreateOrEditTableViewForm = ({
  tableView,
  gridApi,
  createTableView,
  onTableViewCreated,
  updateTableView,
  onTableViewUpdated,
}: CreateOrEditTableViewFormProps) => {
  const creating = isNil(tableView);

  const form = useForm<FormBody>({
    initialValues: {
      name: creating ? '' : tableView.name,
    },
    validate: {
      name: (value) => !value,
    },
  });

  const onSubmit = async (values: typeof form.values) => {
    if (creating) {
      await createTableView({ ...values, gridState: stringifyGridStateJson(gridApi.getState()) }).then(
        onTableViewCreated,
      );
    } else {
      await updateTableView(tableView.tableViewId, {
        ...values,
      }).then(onTableViewUpdated);
    }
  };

  useFormModal(form, onSubmit);

  return (
    <TextInput
      label="Name"
      maxLength={64}
      required
      autoFocus
      data-autofocus={true}
      style={{ paddingBottom: 6 }}
      disabled={form.submitting}
      {...form.getInputProps('name')}
    />
  );
};
